import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AgGridModule } from '@ag-grid-community/angular';
import { ColDef, GridApi, GridReadyEvent, SideBarDef } from '@ag-grid-community/core';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AgFns } from '../ag-grid/ag-fns';
import { GRID_CONFIG } from '../ag-grid/grid-config';
import { UserPrefsService } from '../../services/user-prefs.service';

/**
 * Parameters for one "Analyze" result. The caller loads its own rows and describes its own
 * columns. This dialog owns everything else - the tool panels, the pivot and row group panels,
 * the Excel export and the sizing.
 */
export interface AnalysisGridDialogParams {
  /** Dialog header text, and the Excel sheet name. */
  title: string;
  /** The rows to analyze. */
  rowData: unknown[];
  columnDefs: ColDef[];
  /** Excel file name, without the date stamp or the extension. Defaults to 'analysis'. */
  exportFileName?: string;
  /** Short sentence under the header that states what the numbers mean. Optional. */
  subtitle?: string;
  /** ag-grid groupDefaultExpanded. -1 expands every group. Defaults to 0 (collapsed). */
  groupDefaultExpanded?: number;
}

/**
 * Reusable analysis dialog for the "Analyze" menus.
 *
 * Every Analyze menu option opens this one component. The caller supplies a title, the rows and
 * the column definitions. The dialog supplies the full ag-Grid Enterprise tool set - the column
 * chooser, the filter tool panel, pivot mode, row grouping, range selection and Excel export.
 *
 * RULE: feed this dialog a server DTO. Never pass Breeze entities.
 * Breeze entities are proxied, observable objects. ag-Grid pivot mode and row grouping re-key and
 * aggregate rows as plain data, and they do not behave well over those proxies. Analysis dialogs
 * exist to expose pivot and grouping, so the rows must be flat and read-only. Write a DTO endpoint
 * for each new Analyze option. This is settled - do not weigh Breeze against a DTO here.
 *
 * The host module must register these ag-Grid modules, or the side bar does not appear:
 * ColumnsToolPanelModule, FiltersToolPanelModule, SideBarModule, RowGroupingModule,
 * SetFilterModule, MenuModule, RangeSelectionModule, ExcelExportModule, ClientSideRowModelModule.
 */
@Component({
  selector: 'prox-analysis-grid-dialog',
  standalone: true,
  imports: [CommonModule, AgGridModule, ButtonModule],
  templateUrl: './analysis-grid-dialog.component.html',
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }
    `,
  ],
})
export class AnalysisGridDialogComponent implements OnInit {
  params!: AnalysisGridDialogParams;
  gridApi!: GridApi;

  readonly gridConfig = GRID_CONFIG;

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    enableRowGroup: true,
    enablePivot: true,
    enableValue: true,
  };

  sideBar: SideBarDef = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressRowGroups: false,
          suppressValues: false,
          suppressPivots: false,
          suppressPivotMode: false,
        },
      },
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel',
      },
    ],
    defaultToolPanel: '',
  };

  constructor(
    private config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    public userPrefsService: UserPrefsService,
  ) {}

  ngOnInit(): void {
    this.params = this.config.data as AnalysisGridDialogParams;
    // Applies the standard column formatting - right-aligns Amt/Pct/Count fields, adds formatters.
    AgFns.updateColDefs(this.params.columnDefs);
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
    AgFns.sizeColumnsToFit(this.gridApi);
  }

  onExport(): void {
    const fileName = this.params.exportFileName || 'analysis';
    this.gridApi.exportDataAsExcel({
      fileName: `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`,
      sheetName: this.params.title.substring(0, 31),
    });
  }

  onClose(): void {
    this.ref.close();
  }
}
